import { EditOutlined } from '@ant-design/icons';
import { designTokens } from '@app/theme';
import { useCan, useList, useUpdate } from '@refinedev/core';
import { Button, Card, Empty, Grid, Select, Space, Spin, Tag, Tooltip, Typography } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import type { SettingResponse } from '@api/generated/types.gen';

import { SettingEditor } from '../components/SettingEditor';
import { isNumericType, settingTypeTagColor } from '../setting-type';

const { Title, Text } = Typography;

/**
 * Ключи, оставленные на бэке только ради legacy-полей: править их смысла нет.
 * `payment.fee.amount` вытеснен тарифной сеткой (раздел «Тарифы комиссии»).
 */
const DEPRECATED_KEYS = new Set(['payment.fee.amount']);

type SettingsGroup = {
  /** Префикс ключа до первой точки (`otp.length` → `otp`) или `other`. */
  prefix: string;
  items: SettingResponse[];
};

/**
 * Группирует настройки по неймспейсу ключа (префикс до первой точки).
 * Порядок групп — по первому вхождению в ответе бэкенда, ключи внутри
 * группы сортируются алфавитно.
 */
function groupSettings(settings: SettingResponse[]): SettingsGroup[] {
  const byPrefix = new Map<string, SettingResponse[]>();
  for (const s of settings) {
    const key = s.key ?? '';
    const prefix = key.includes('.') ? key.slice(0, key.indexOf('.')) : 'other';
    const bucket = byPrefix.get(prefix);
    if (bucket) {
      bucket.push(s);
    } else {
      byPrefix.set(prefix, [s]);
    }
  }
  return [...byPrefix.entries()].map(([prefix, items]) => ({
    prefix,
    items: [...items].sort((a, b) => (a.key ?? '').localeCompare(b.key ?? '')),
  }));
}

/**
 * Список бизнес-настроек `app_settings`, сгруппированный по неймспейсу ключа.
 *
 * Показывается одна группа, выбранная селектом (выбор хранится в `?group=`,
 * так что переживает обновление страницы). Каждая настройка — вертикальная
 * карточка-строка: «ключ + тип + действие», описание, значение отдельным
 * блоком (длинный текст обрезается до двух строк — целиком виден в редакторе).
 * Такая структура одинаково работает на десктопе и мобильном.
 * Кнопка редактирования видна только при праве `settings.write`;
 * редактирование — в боковом drawer, адаптированном под `type` настройки.
 */
export function SettingsListPage() {
  const { t } = useTranslation(['settings', 'common']);
  const { result, query } = useList<SettingResponse>({ resource: 'settings' });
  const { data: canEdit } = useCan({ resource: 'settings', action: 'edit' });
  const updateMutation = useUpdate<SettingResponse>();
  const [editing, setEditing] = useState<SettingResponse | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const screens = Grid.useBreakpoint();

  const groups = useMemo(() => groupSettings(result.data), [result.data]);

  const groupLabel = (prefix: string): string =>
    t(`settings:groups.${prefix}`, { defaultValue: prefix });

  const activeGroup =
    groups.find((g) => g.prefix === searchParams.get('group')) ?? groups[0] ?? null;

  const onSelectGroup = (prefix: string): void => {
    setSearchParams(
      (prev) => {
        prev.set('group', prefix);
        return prev;
      },
      { replace: true },
    );
  };

  const onSave = (key: string, value: string): void => {
    updateMutation.mutate(
      {
        resource: 'settings',
        id: key,
        values: { value },
        successNotification: () => ({
          message: t('settings:edit.saved'),
          type: 'success',
        }),
      },
      {
        onSuccess: () => {
          setEditing(null);
        },
      },
    );
  };

  const renderValue = (s: SettingResponse) => {
    if (s.type === 'BOOLEAN') {
      const on = s.value === 'true';
      return (
        <Tag color={on ? 'success' : 'default'} style={{ marginInlineEnd: 0, fontWeight: 500 }}>
          {t(`settings:boolean.${on ? 'true' : 'false'}`)}
        </Tag>
      );
    }
    return (
      <div
        style={{
          padding: '6px 10px',
          background: designTokens.slate50,
          border: `1px solid ${designTokens.slate100}`,
          borderRadius: 6,
        }}
      >
        <Text
          style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontVariantNumeric: 'tabular-nums',
            fontSize: 13,
            fontWeight: 500,
            color: isNumericType(s.type) ? designTokens.amber600 : designTokens.navy900,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
          }}
        >
          {s.value}
        </Text>
      </div>
    );
  };

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <div>
        <Title level={3} style={{ marginBlockEnd: 4, fontWeight: 600 }}>
          {t('settings:title')}
        </Title>
        <Text type="secondary" style={{ fontSize: 14 }}>
          {t('settings:subtitle')}
        </Text>
      </div>

      <Spin spinning={query.isLoading}>
        {!query.isLoading && groups.length === 0 ? (
          <Card style={{ borderColor: designTokens.slate200 }}>
            <Empty description={t('common:states.empty')} />
          </Card>
        ) : (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Select
              value={activeGroup?.prefix}
              onChange={onSelectGroup}
              options={groups.map((g) => ({
                value: g.prefix,
                label: `${groupLabel(g.prefix)} · ${String(g.items.length)}`,
              }))}
              style={{ width: '100%', maxWidth: 320 }}
              aria-label={t('settings:groupLabel')}
            />
            {activeGroup && (
              <Card style={{ borderColor: designTokens.slate200 }} styles={{ body: { padding: 0 } }}>
                {activeGroup.items.map((s, idx) => (
                  <div
                    key={s.key}
                    style={{
                      padding: '12px 16px',
                      borderBlockStart: idx > 0 ? `1px solid ${designTokens.slate100}` : 'none',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 8,
                      }}
                    >
                      <Space size={8} wrap style={{ minWidth: 0 }}>
                        <Text
                          code
                          style={{
                            fontSize: 13,
                            fontFamily: '"JetBrains Mono", monospace',
                            color: designTokens.navy900,
                            background: designTokens.slate50,
                            padding: '2px 6px',
                            borderRadius: 4,
                            overflowWrap: 'anywhere',
                          }}
                        >
                          {s.key}
                        </Text>
                        <Tag
                          color={settingTypeTagColor(s.type)}
                          style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.3 }}
                        >
                          {t(`settings:types.${s.type ?? 'STRING'}`, { defaultValue: s.type })}
                        </Tag>
                        {DEPRECATED_KEYS.has(s.key ?? '') && (
                          <Tooltip title={t('settings:deprecatedHint')}>
                            <Tag
                              color="warning"
                              style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.3 }}
                            >
                              {t('settings:deprecated')}
                            </Tag>
                          </Tooltip>
                        )}
                      </Space>
                      {canEdit?.can && (
                        <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          aria-label={t('common:actions.edit')}
                          style={{ flexShrink: 0 }}
                          onClick={() => {
                            setEditing(s);
                          }}
                        >
                          {screens.md ? t('common:actions.edit') : null}
                        </Button>
                      )}
                    </div>

                    {s.description && (
                      <div style={{ marginBlockStart: 4 }}>
                        <Text style={{ fontSize: 13, color: designTokens.slate500 }}>
                          {s.description}
                        </Text>
                      </div>
                    )}

                    <div style={{ marginBlockStart: 8 }}>{renderValue(s)}</div>
                  </div>
                ))}
              </Card>
            )}
          </Space>
        )}
      </Spin>

      <SettingEditor
        setting={editing}
        saving={updateMutation.mutation.isPending}
        onCancel={() => {
          setEditing(null);
        }}
        onSave={onSave}
      />
    </Space>
  );
}
